/**
 * processTree.js
 * Pure, framework-free projection of the Workflow 2.0 editor model (`workflow.nodes`) into the rows of
 * the read-only process Tree surface (story 118). Same pattern as `bomTree.js`/`cascadeApproval.js`:
 * no React, named exports, one unit test file (`__tests__/processTree.test.js`).
 *
 * The tree is a SIBLING projection of the same model the graph projects (`utils.js#getWorkflowNodesAndEdges`):
 * it reads `nodeResults` as the edges, never reorders them (RN11) and never writes to the model (RN7).
 * Formatting is NOT done here: rows carry milliseconds and the components format them (`GetUtcTime` for
 * job rows, `GetAccumulatedUtcTime` for aggregates — RN29.1/RN29.2).
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').NodeResult} NodeResult
 */
import { resolveJobMaterialRows, summarizeJobMaterial } from './processTreeCoverage'
import { isExternalEffective } from './jobExternalCharacter'

/** The five row variants of the projection (03-dominio.md §3.1). Only JOB and SET exist in the model. */
const PROCESS_TREE_ROW_KIND = {
  JOB: 'JOB',
  SET: 'SET',
  PARALLEL: 'PARALLEL',
  BRANCH: 'BRANCH',
  MATERIAL: 'MATERIAL',
  // PO review 2026-09-21: el pie «+X materials» de un job cuyo material esta recogido. Informativo y
  // pulsable, nunca seleccionable: abre el resto de lineas de ESE job, no de los demas.
  MATERIAL_MORE: 'MATERIAL_MORE',
  REFERENCE: 'REFERENCE'
}

/**
 * Lo que le pasa al camino EN esta fila. `SPLIT` es la fila que anuncia que el proceso se parte y
 * `MERGE` la operacion donde los caminos se vuelven a juntar; el resto no cambian el dibujo.
 */
const LANE_EVENT = {
  SPLIT: 'SPLIT',
  MERGE: 'MERGE'
}

/** RN16: the branch a row hangs from. FAIL is drawn as rework. */
const BRANCH = {
  OK: 'OK',
  FAIL: 'FAIL'
}

const DURATION_PATTERN = /^\d+:\d{1,2}:\d{1,2}$/

/**
 * RN28.1: a task duration is valid only when it is a `hh:mm:ss` string with three numeric parts. Both
 * invalid cases the code produces are discarded here: the one that makes `durationStringToMilliseconds`
 * throw (null/undefined -> `.split` on nothing) and the one that makes it RETURN A TEXT instead of a
 * number (fewer than three parts, `DateTimeFunctions.jsx:167-174`). That second case is exactly what the
 * job properties panel fails to catch and paints as `NaN:NaN:NaN` (RN28.2, bug kept out of this story).
 * @param {*} theoreticalTime
 * @returns {number|null} milliseconds, or null when the duration is not valid
 */
const parseTaskDurationMs = (theoreticalTime) => {
  if (typeof theoreticalTime !== 'string' || !DURATION_PATTERN.test(theoreticalTime.trim())) {
    return null
  }
  const [hours, minutes, seconds] = theoreticalTime.trim().split(':').map(Number)
  return (hours * 3600 + minutes * 60 + seconds) * 1000
}

/**
 * RN28/RN28.1/RN30: the time of a job is the sum of the VALID `theoreticalTime` of its tasks, in
 * milliseconds. `isUnknown` is true when nothing valid adds up (no tasks, no valid duration, or a valid
 * sum of zero): zero and "no data" are drawn the same way, a grey dash, never `00:00:00` (POL2).
 * @param {JobReference|null|undefined} jobReference
 * @returns {{ ms: number, isUnknown: boolean }}
 */
const computeJobTimeMs = (jobReference) => {
  const tasks = Array.isArray(jobReference?.taskReferences) ? jobReference.taskReferences : []
  const ms = tasks.reduce((total, taskReference) => {
    const duration = parseTaskDurationMs(taskReference?.theoreticalTime)
    return duration === null ? total : total + duration
  }, 0)
  return { ms, isUnknown: ms === 0 }
}

const findNodeInLevel = (nodes, nodeId) => (nodes || []).find((node) => node.id === nodeId) || null

const isSetNode = (node) => node?.nodeType === 'WORKFLOW'
const isHydratedSet = (node) => isSetNode(node) && Array.isArray(node.subWorkflow?.nodes)

/**
 * RN33/RN57: every JOB node of every HYDRATED level, deduplicated by id, in model order (depth first).
 * Skipped jobs are NOT filtered out (RN33.1/D46): the totals count them and sum their time.
 * @param {Node[]} nodes Top-level nodes
 * @returns {Node[]}
 */
const collectJobNodes = (nodes) => {
  const seen = new Set()
  const jobs = []
  const visit = (level) => {
    (level || []).forEach((node) => {
      if (isSetNode(node)) {
        visit(node.subWorkflow?.nodes)
        return
      }
      if (node && node.nodeType === 'JOB' && !seen.has(node.id)) {
        seen.add(node.id)
        jobs.push(node)
      }
    })
  }
  visit(nodes)
  return jobs
}

/** Ids of the WORKFLOW nodes (any depth) whose subworkflow content is not loaded. */
const collectUnhydratedSetNodeIds = (nodes) => {
  const ids = []
  const visit = (level) => {
    (level || []).forEach((node) => {
      if (!isSetNode(node)) {
        return
      }
      if (isHydratedSet(node)) {
        visit(node.subWorkflow.nodes)
      } else {
        ids.push(node.id)
      }
    })
  }
  visit(nodes)
  return ids
}

const sumJobTimeMs = (jobs) => jobs.reduce((total, node) => total + computeJobTimeMs(node.jobReference).ms, 0)

/**
 * RN31: the figures of a set row — how many distinct jobs it holds at ANY depth and the sum of their
 * time (skipped included, RN33.1). `hydrated: false` when its subworkflow is not loaded: then there is
 * nothing to count and the row shows dashes, never zeros (POL2).
 * @param {Node} setNode
 * @returns {{ jobCount: number, timeMs: number, hydrated: boolean }}
 */
const computeSetSummary = (setNode) => {
  if (!isHydratedSet(setNode)) {
    return { jobCount: 0, timeMs: 0, hydrated: false }
  }
  const jobs = collectJobNodes(setNode.subWorkflow.nodes)
  return { jobCount: jobs.length, timeMs: sumJobTimeMs(jobs), hydrated: true }
}

/**
 * RN33/RN33.1/RN33.2/RN34: the header figures of the COMPLETE process — distinct jobs at any depth and
 * their summed time, skipped jobs included, reference rows excluded (they are the same job). Depends on
 * the model alone: collapsing and searching cannot change it (POL5). `incomplete` is true when some set
 * could not be hydrated (RN36), which is what raises the "total incomplete" warning.
 * @param {Workflow} workflow
 * @param {{ failedSetNodeIds?: string[] }} [options]
 * @returns {{ jobCount: number, totalTimeMs: number, incomplete: boolean, unhydratedSetIds: string[] }}
 */
const collectProcessTotals = (workflow, options = {}) => {
  const jobs = collectJobNodes(workflow?.nodes)
  const unhydratedSetIds = collectUnhydratedSetNodeIds(workflow?.nodes)
  const failed = Array.isArray(options.failedSetNodeIds) ? options.failedSetNodeIds : []
  const pendingIds = [...new Set([...unhydratedSetIds, ...failed])]
  return {
    jobCount: jobs.length,
    totalTimeMs: sumJobTimeMs(jobs),
    incomplete: pendingIds.length > 0,
    unhydratedSetIds: pendingIds
  }
}

/**
 * RN10/RN19: the roots of a level are its `isFirst` nodes in model order; `unreachable` are the nodes of
 * the level that no walk from those roots can reach (following `nextNode` of ANY result, within the
 * level), in model order. A level with no `isFirst` has no roots and every node is unreachable (S22).
 * Reachability is computed from the roots — not merely "somebody points at me" — because a node that is
 * only pointed at by an unreachable node would otherwise be neither walked nor listed (POL1).
 * @param {Node[]} nodes Nodes of one level
 * @returns {{ roots: Node[], unreachable: Node[] }}
 */
const resolveLevelRoots = (nodes) => {
  const level = Array.isArray(nodes) ? nodes : []
  const roots = level.filter((node) => node.isFirst === true)
  const reachable = new Set()
  const pending = roots.map((node) => node.id)
  while (pending.length > 0) {
    const nodeId = pending.shift()
    if (reachable.has(nodeId)) {
      continue
    }
    const node = findNodeInLevel(level, nodeId)
    if (!node) {
      continue
    }
    reachable.add(nodeId);
    (node.nodeResults || []).forEach((nodeResult) => {
      if (nodeResult.nextNode && !reachable.has(nodeResult.nextNode)) {
        pending.push(nodeResult.nextNode)
      }
    })
  }
  return { roots, unreachable: level.filter((node) => !reachable.has(node.id)) }
}

/**
 * RN11-RN13: the children of a node are its `nodeResults` in MODEL ORDER. Each one resolves within the
 * level (`utils.js:144`): `target` is the node it points to, or null when the result ends the process
 * (`nextNode === null`, RN12) or when it does not resolve — that second case is a DANGLING link (RN13),
 * which the graph would draw as an end and this lens refuses to (POL2).
 * @param {Node} node
 * @param {Node[]} nodes Nodes of the same level
 * @returns {{ nodeResult: NodeResult, target: Node|null, dangling: boolean, branch: string }[]}
 */
const resolveChildBranches = (node, nodes) => {
  return (node?.nodeResults || []).map((nodeResult) => {
    const target = nodeResult.nextNode ? findNodeInLevel(nodes, nodeResult.nextNode) : null
    return {
      nodeResult,
      target,
      dangling: Boolean(nodeResult.nextNode) && !target,
      branch: nodeResult.operationResult === 'OK' ? BRANCH.OK : BRANCH.FAIL
    }
  })
}

/**
 * Ordered list of the nodes a branch can reach going forward (following `nextNode` of ANY result, within
 * the level), starting with the branch's own first node. Uses its own visited set, so a cycle inside the
 * branch never hangs it.
 */
const collectForwardReach = (startNode, nodes) => {
  const order = []
  const seen = new Set()
  const pending = [startNode]
  while (pending.length > 0) {
    const node = pending.shift()
    if (!node || seen.has(node.id)) {
      continue
    }
    seen.add(node.id)
    order.push(node.id);
    (node.nodeResults || []).forEach((nodeResult) => {
      const target = nodeResult.nextNode ? findNodeInLevel(nodes, nodeResult.nextNode) : null
      if (target && !seen.has(target.id)) {
        pending.push(target)
      }
    })
  }
  return order
}

/**
 * D44/RN18.1 (risk R13): the operation where ALL the branches of a parallel group meet again, resolved
 * BEFORE the branches are walked so it can act as a barrier for every one of them. For each branch the
 * forward-reachable nodes are collected (own visited set, cycle-safe); the join is the first node, in the
 * order of the first branch, that belongs to every branch's set. No common node, or a candidate already
 * developed (`visited`), means no join: then RN18/RN18.2 rule (first arrival develops, later ones refer).
 * @param {Node[]} branchTargets First node of each OK branch, in model order
 * @param {Node[]} nodes Nodes of the level
 * @param {Set<string>} visited Node ids already developed by the walk
 * @returns {string|null} The join node id, or null
 */
const resolveParallelJoin = (branchTargets, nodes, visited) => {
  const reaches = (branchTargets || []).map((target) => collectForwardReach(target, nodes))
  if (reaches.length < 2) {
    return null
  }
  const [first, ...others] = reaches
  const otherSets = others.map((reach) => new Set(reach))
  const join = first.find((nodeId) => otherSets.every((reach) => reach.has(nodeId)))
  if (!join || (visited && visited.has(join))) {
    return null
  }
  return join
}

const labelOf = (node) => node.code || node.name || node.id

/**
 * Builds the JOB/SET/REFERENCE row of a model node. POL4: every missing mark reads as false/empty.
 */
const buildNodeRow = (node, { kind, key, parentKey, depth, branch, extra }) => {
  const isSet = node.nodeType === 'WORKFLOW'
  const jobReference = node.jobReference || {}
  const setSummary = isSet ? computeSetSummary(node) : null
  const time = isSet
    ? { ms: setSummary.timeMs, isUnknown: !setSummary.hydrated || setSummary.timeMs === 0 }
    : computeJobTimeMs(jobReference)
  return {
    kind,
    key,
    parentKey,
    depth,
    nodeId: node.id,
    name: node.name || '',
    code: node.code || '',
    technology: isSet ? '' : (jobReference.technology?.name || ''),
    timeMs: time.ms,
    timeIsUnknown: time.isUnknown,
    branch,
    skipped: node.skipped === true,
    external: !isSet && jobReference.external === true,
    skipTrace: {
      skipReason: node.skipReason || '',
      skippedBy: node.skippedBy || '',
      skippedAt: node.skippedAt || ''
    },
    dangling: false,
    unreachable: false,
    hoistedJoin: false,
    failedHydration: false,
    collapsible: kind === PROCESS_TREE_ROW_KIND.SET,
    isLast: false,
    contentOf: '',
    laneEvent: '',
    inParallel: false,
    branchHead: false,
    branchLast: false,
    branchIndex: 0,
    branchCount: 0,
    splitNodeId: '',
    joinNodeId: '',
    referenceBranchIndex: 0,
    jobCount: isSet ? setSummary.jobCount : 0,
    material: null,
    materialCodes: [],
    ...extra
  }
}

/**
 * RN14/RN15: the synthetic "Parallel · N branches" row. Nothing of the model: no id, no figures, not
 * selectable; it only groups (and folds) the OK branches that leave the same operation together.
 */
const buildParallelRow = ({ key, parentKey, depth, branchCount, contentOf = '', splitNodeId = '', joinNodeId = '' }) => ({
  kind: PROCESS_TREE_ROW_KIND.PARALLEL,
  key,
  parentKey,
  depth,
  nodeId: '',
  name: '',
  code: '',
  technology: '',
  timeMs: 0,
  timeIsUnknown: true,
  branch: BRANCH.OK,
  skipped: false,
  external: false,
  skipTrace: { skipReason: '', skippedBy: '', skippedAt: '' },
  dangling: false,
  unreachable: false,
  hoistedJoin: false,
  failedHydration: false,
  collapsible: true,
  isLast: false,
  contentOf,
  laneEvent: LANE_EVENT.SPLIT,
  inParallel: false,
  branchHead: false,
  branchLast: false,
  branchIndex: 0,
  referenceBranchIndex: 0,
  branchCount,
  splitNodeId,
  joinNodeId,
  material: null,
  materialCodes: []
})

/**
 * La cabecera de un camino de un grupo paralelo. Existe porque fiar la frontera entre caminos a una
 * marca de dos pixeles no se lee: con ella se ve de un golpe donde empieza cada camino y que operaciones
 * son suyas. Sintetica, como la fila Paralelo: no es un nodo, no se selecciona y no lleva cifras.
 */
/**
 * `splitNodeId` is the operation the group opens from ('' when the branches are the roots of a level, which
 * open from the start of the process) and `joinNodeId` the operation they converge on ('' when they run to
 * the end). They exist so a branch can be inspected as a node: where it comes from, where it goes.
 */
const buildBranchRow = ({ key, parentKey, depth, branchIndex, branchCount, splitNodeId = '', joinNodeId = '' }) => ({
  kind: PROCESS_TREE_ROW_KIND.BRANCH,
  key,
  parentKey,
  depth,
  nodeId: '',
  name: '',
  code: '',
  technology: '',
  timeMs: 0,
  timeIsUnknown: true,
  branch: BRANCH.OK,
  skipped: false,
  external: false,
  skipTrace: { skipReason: '', skippedBy: '', skippedAt: '' },
  dangling: false,
  unreachable: false,
  hoistedJoin: false,
  failedHydration: false,
  collapsible: true,
  isLast: false,
  contentOf: '',
  laneEvent: '',
  inParallel: true,
  branchHead: true,
  branchLast: false,
  branchIndex,
  branchCount,
  splitNodeId,
  joinNodeId,
  jobCount: 0,
  material: null,
  materialCodes: []
})

/**
 * RN54/RN156: a material row — SELECTABLE since the PO review of 2026-09-21 (RN156 reverses RN53 of
 * story #118: the row cuts its name, so clicking it is how the whole one is read), and never
 * collapsible. Its `key` hangs from the job row so it folds with it; its identity is the BOM line.
 */
/** PO review 2026-09-21: cuantas lineas de material ensena un job antes de recoger el resto. */
const MATERIAL_PREVIEW_COUNT = 1

const buildMaterialRow = (materialRow, { parentKey, depth, materialIndex = 0 }) => ({
  kind: PROCESS_TREE_ROW_KIND.MATERIAL,
  key: parentKey + '/mat/' + materialRow.key,
  parentKey,
  depth,
  nodeId: '',
  name: materialRow.name,
  code: materialRow.code,
  technology: '',
  timeMs: 0,
  timeIsUnknown: true,
  branch: null,
  skipped: false,
  external: false,
  skipTrace: { skipReason: '', skippedBy: '', skippedAt: '' },
  dangling: false,
  unreachable: false,
  hoistedJoin: false,
  failedHydration: false,
  collapsible: false,
  isLast: false,
  contentOf: '',
  laneEvent: '',
  inParallel: false,
  branchHead: false,
  branchLast: false,
  branchIndex: 0,
  referenceBranchIndex: 0,
  jobCount: 0,
  material: null,
  materialCodes: [],
  materialRow,
  // Posicion dentro del material de SU job: la fila la usa para escalonar la caida, de modo que las
  // lineas bajen en cascada y no todas a la vez.
  materialIndex
})

/**
 * PO review 2026-09-21: el pie «+X materials» de un job que muestra solo su primera linea. Cuelga del
 * mismo job que las lineas, asi que se pliega con el como ellas, y guarda en `hiddenMaterialCount`
 * cuantas quedan por ver. `materialOwnerKey` es a quien abre el clic — la fila no se abre a si misma.
 */
const buildMaterialMoreRow = ({ parentKey, depth, hiddenCount, materialIndex }) => ({
  ...buildMaterialRow({ key: 'more', name: '', code: '', position: '', quantity: null, state: null }, { parentKey, depth, materialIndex }),
  kind: PROCESS_TREE_ROW_KIND.MATERIAL_MORE,
  key: parentKey + '/mat-more',
  materialRow: null,
  materialOwnerKey: parentKey,
  hiddenMaterialCount: hiddenCount
})

/**
 * RN10-RN19: projects the whole process (every hydrated level) into its ordered list of rows.
 *
 * Walk, per node: mark visited and emit its row; a SET hangs the roots of its subworkflow (RN17); then its
 * OK branches and, after them, its FAIL branches (RN16), each resolved in model order (RN11). The visited
 * guard (RN18) develops each `node.id` once: a second arrival emits a REFERENCE row and does not recurse,
 * which is what makes the walk terminate on a real cycle and keeps the row count equal to the header's
 * job count (INV1/INV2). Unreachable nodes are listed at the end of their level, flat, in model order,
 * and reported in the incomplete-process warning together with the dangling links (RN19/RN13/RN71).
 *
 * Material (RN50/RN52-RN54/RN59) enters as an OPTIONAL `coverage` context: with it, every JOB row gets its
 * material summary and codes and its MATERIAL rows, hung right under the job — one line plus the
 * `+X materials` foot while it is collapsed (RN210); without it, `material` is null — "no data", not zero — and no material row exists. Sets never
 * carry material (RN52).
 *
 * @param {Object} params
 * @param {Workflow} params.workflow The (possibly hydrated) editor model — read only
 * @param {import('./processTreeCoverage').CoverageContext|null} [params.coverage] Only while the Material button is on
 * @param {string[]} [params.failedSetNodeIds] WORKFLOW node ids whose subworkflow could not be loaded (RN36)
 * @param {boolean} [params.detailedBranches] Con `true`, cada camino de un grupo paralelo abre su propia
 *   cabecera y la fila «Paralelo» los anuncia. Con `false` (por defecto) las operaciones van seguidas y
 *   lo unico que recuerda que hay caminos es la marca de la columna izquierda. Lo decide una setting de
 *   la instalacion: hay clientes que meten todo en paralelo porque la secuencia les da igual, y para
 *   ellos el detalle es ruido.
 * @returns {{ rows: Object[], warnings: { incompleteProcess: { rowKeys: string[], labels: string[] }, incompleteTotal: { rowKeys: string[], labels: string[] } } }}
 */
const buildProcessTreeRows = ({ workflow, coverage = null, failedSetNodeIds = [], detailedBranches = false, expandedMaterialKeys = new Set() }) => {
  const rows = []
  const visited = new Set()
  const warnings = {
    incompleteProcess: { rowKeys: [], labels: [] },
    incompleteTotal: { rowKeys: [], labels: [] }
  }
  const failedSets = new Set(failedSetNodeIds)

  const emit = (row) => {
    rows.push(row)
    return row
  }

  const markIncompleteProcess = (row, node) => {
    if (!warnings.incompleteProcess.rowKeys.includes(row.key)) {
      warnings.incompleteProcess.rowKeys.push(row.key)
      warnings.incompleteProcess.labels.push(labelOf(node))
    }
  }

  const closeParent = (parentKey) => {
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      if (rows[index].parentKey === parentKey) {
        rows[index].isLast = true
        return
      }
    }
  }

  const walkLevel = (nodes, depth, parentKey, contentOf = '') => {
    const { roots, unreachable } = resolveLevelRoots(nodes)
    // RN14.1: varias operaciones de arranque en el MISMO nivel son caminos simultaneos, aunque ninguna
    // operacion se parta en dos. Es como el grafo lo dibuja —START cuelga de todas— y es la forma real
    // de FAT-1 en ASCOD_LETONIA: tres inspecciones a la vez. Antes se listaban como si fueran una
    // detras de otra, que es sencillamente falso.
    if (roots.length >= 2 && detailedBranches) {
      const rootsKey = parentKey + '/roots'
      // Where the root branches converge, if they do. Resolved to NAME it in the inspector, not to
      // hoist it: the walk keeps no barrier here, so the rows are exactly what they were.
      const rootsJoin = resolveParallelJoin(roots, nodes, new Set()) || ''
      emit(buildParallelRow({ key: rootsKey, parentKey, depth, branchCount: roots.length, contentOf, joinNodeId: rootsJoin }))
      roots.forEach((root, index) => {
        const branchKey = rootsKey + '/b' + index
        emit(buildBranchRow({ key: branchKey, parentKey: rootsKey, depth, branchIndex: index + 1, branchCount: roots.length, joinNodeId: rootsJoin }))
        walkNode(root, nodes, depth + 1, branchKey, null, new Set(), { inParallel: true, branchIndex: index + 1, branchCount: roots.length })
        closeParent(branchKey)
      })
      closeParent(rootsKey)
    } else if (roots.length >= 2) {
      // Modo simplificado: sin fila de grupo ni cabeceras. Las operaciones van seguidas y la marca de
      // la columna es lo unico que dice que arrancan a la vez.
      roots.forEach((root, index) => walkNode(root, nodes, depth, parentKey, null, new Set(), {
        contentOf, inParallel: true, branchHead: true, branchIndex: index + 1, branchCount: roots.length
      }))
    } else {
      roots.forEach((root) => walkNode(root, nodes, depth, parentKey, null, new Set(), { contentOf }))
    }
    unreachable.forEach((node) => {
      if (visited.has(node.id)) {
        return
      }
      const row = walkNode(node, nodes, depth, parentKey, null, new Set(), { contentOf, unreachable: true })
      if (row) {
        markIncompleteProcess(row, node)
      }
    })
  }

  /**
   * @param {Set<string>} barriers RN18.1: join nodes of the parallel groups this walk is inside of. A branch
   * that reaches one of them dies there WITHOUT emitting anything — the join is developed after the group.
   */
  const walkNode = (node, nodes, depth, parentKey, branch, barriers, extra = {}) => {
    if (barriers.has(node.id)) {
      return null
    }
    // Un camino se hereda: los pasos que siguen a la cabeza de un camino siguen dentro de el.
    const inParallel = extra.inParallel === true
    const branchIndex = extra.branchIndex || 0
    const branchCount = extra.branchCount || 0
    if (visited.has(node.id)) {
      // La operacion ya salio antes. Se anota EN QUE CAMINO se desarrollo, para que la fila de
      // referencia pueda decirlo en vez de dejar al lector buscando un duplicado por la lista.
      const developed = rows.find((candidate) => candidate.nodeId === node.id && candidate.kind !== PROCESS_TREE_ROW_KIND.REFERENCE)
      emit(buildNodeRow(node, {
        kind: PROCESS_TREE_ROW_KIND.REFERENCE,
        key: parentKey + '/ref/' + node.id,
        parentKey,
        depth,
        branch,
        extra: { ...extra, collapsible: false, timeIsUnknown: true, timeMs: 0, referenceBranchIndex: developed ? developed.branchIndex : 0 }
      }))
      return null
    }
    visited.add(node.id)
    const isSet = node.nodeType === 'WORKFLOW'
    const key = parentKey + '/' + node.id
    const row = emit(buildNodeRow(node, {
      kind: isSet ? PROCESS_TREE_ROW_KIND.SET : PROCESS_TREE_ROW_KIND.JOB,
      key,
      parentKey,
      depth,
      branch,
      extra
    }))

    if (isSet) {
      const subNodes = node.subWorkflow?.nodes
      if (Array.isArray(subNodes)) {
        walkLevel(subNodes, depth + 1, key, key)
      } else if (failedSets.has(node.id)) {
        row.failedHydration = true
        warnings.incompleteTotal.rowKeys.push(key)
        warnings.incompleteTotal.labels.push(labelOf(node))
      }
    } else if (coverage) {
      const materialRows = resolveJobMaterialRows(node, coverage)
      row.material = summarizeJobMaterial(node, coverage)
      row.materialCodes = materialRows.map((materialRow) => materialRow.code)
      // PO review 2026-09-21: un job ensena UNA linea y un pie con las que faltan; abierto, las ensena
      // todas. `materialCodes` se queda entero a proposito: la busqueda encuentra un componente por
      // codigo aunque su linea este recogida, igual que encuentra dentro de un conjunto plegado.
      const expanded = expandedMaterialKeys.has(key)
      const shown = expanded ? materialRows : materialRows.slice(0, MATERIAL_PREVIEW_COUNT)
      shown.forEach((materialRow, materialIndex) => emit(buildMaterialRow(materialRow, { parentKey: key, depth: depth + 1, materialIndex })))
      const hiddenCount = materialRows.length - shown.length
      // El pie sale en los dos sentidos: con lineas escondidas las baja, y ya abierto vuelve a recoger.
      // Sin el, abrir el material de un job era un viaje de ida (PO, 2026-09-21).
      if (hiddenCount > 0 || expanded) {
        emit(buildMaterialMoreRow({ parentKey: key, depth: depth + 1, hiddenCount, materialIndex: shown.length }))
      }
    }

    const branches = resolveChildBranches(node, nodes)
    if (branches.some((child) => child.dangling)) {
      row.dangling = true
      markIncompleteProcess(row, node)
    }
    // Unreachable nodes are listed flat: their own links were either walked from the roots already or
    // will be listed in the same block (RN19), so following them here would only duplicate rows.
    if (!extra.unreachable) {
      const okBranches = branches.filter((child) => child.branch === BRANCH.OK && child.target)
      const failBranches = branches.filter((child) => child.branch === BRANCH.FAIL && child.target)
      // El escalon significa UNA cosa: estar dentro de la fila de arriba (el contenido de un conjunto,
      // o el material de un job). Ni la secuencia ni los caminos escalonan: los caminos viven en el
      // CARRIL, la columna estrecha de la izquierda. Por eso los nombres no se mueven por mucho que el
      // proceso se alargue, que era el defecto de fondo: 25 conjuntos ENCADENADOS bajaban en diagonal
      // como si estuviesen anidados.
      if (okBranches.length >= 2) {
        // RN14/RN15: dos o mas resultados OK que resuelven se agrupan bajo una fila sintetica Paralelo.
        // La barrera NO es cosa de sangria: existe para que el join se dibuje UNA vez y no se lo trague
        // el primer camino. Sin ella el resto del proceso colgaria de la rama 1 y plegar el grupo
        // paralelo escondaria el proceso entero.
        const join = resolveParallelJoin(okBranches.map((child) => child.target), nodes, visited)
        const parallelKey = key + '/parallel'
        const branchBarriers = join ? new Set([...barriers, join]) : barriers
        if (detailedBranches) {
          // UNA columna para todo el grupo: cada camino abre su cabecera y sus operaciones cuelgan de
          // ella. Un escalon para el grupo entero, sean 3 caminos o 15: no hay diagonal.
          emit(buildParallelRow({ key: parallelKey, parentKey: key, depth, branchCount: okBranches.length, splitNodeId: node.id, joinNodeId: join || '' }))
          okBranches.forEach((child, index) => {
            const branchKey = parallelKey + '/b' + index
            emit(buildBranchRow({ key: branchKey, parentKey: parallelKey, depth, branchIndex: index + 1, branchCount: okBranches.length, splitNodeId: node.id, joinNodeId: join || '' }))
            walkNode(child.target, nodes, depth + 1, branchKey, BRANCH.OK, branchBarriers, { inParallel: true, branchIndex: index + 1, branchCount: okBranches.length })
            closeParent(branchKey)
          })
          closeParent(parallelKey)
        } else {
          okBranches.forEach((child, index) => walkNode(child.target, nodes, depth, key, BRANCH.OK, branchBarriers, {
            inParallel: true, branchHead: true, branchIndex: index + 1, branchCount: okBranches.length
          }))
        }
        if (join) {
          // El join no pertenece a ningun camino: sale del grupo y vuelve a la vertical.
          walkNode(findNodeInLevel(nodes, join), nodes, depth, key, BRANCH.OK, barriers, { hoistedJoin: true, laneEvent: LANE_EVENT.MERGE })
        }
      } else {
        okBranches.forEach((child) => walkNode(child.target, nodes, depth, key, BRANCH.OK, barriers, { inParallel, branchIndex, branchCount }))
      }
      failBranches.forEach((child) => walkNode(child.target, nodes, depth, key, BRANCH.FAIL, barriers, { inParallel, branchIndex, branchCount }))
    }
    closeParent(key)
    return row
  }

  walkLevel(workflow?.nodes || [], 0, '')
  closeParent('')

  // El codo que cierra el grupo va en la ULTIMA FILA del grupo, no en la cabeza del ultimo camino: si
  // ese camino sigue con mas operaciones, la linea tiene que seguir bajando hasta la ultima de todas.
  rows.forEach((row, index) => {
    const next = rows[index + 1]
    row.branchLast = row.inParallel === true && (!next || next.inParallel !== true)
  })

  return { rows, warnings }
}

/**
 * RN69: the level (array of nodes) a node lives in — the first level, or the `subWorkflow.nodes` of the
 * set that contains it, at any depth. Links are resolved within that array only (`utils.js:144`), which
 * is exactly the set of edges the graph draws for it. Returns the SAME array instance, and an empty
 * array when the node is nowhere.
 * @param {string} nodeId
 * @param {Node[]} nodes Top-level nodes
 * @returns {Node[]}
 */
const findLevelNodes = (nodeId, nodes) => {
  const search = (level) => {
    if (!Array.isArray(level)) {
      return null
    }
    if (level.some((node) => node.id === nodeId)) {
      return level
    }
    for (const node of level) {
      if (isHydratedSet(node)) {
        const found = search(node.subWorkflow.nodes)
        if (found) {
          return found
        }
      }
    }
    return null
  }
  return search(nodes) || []
}

/**
 * RN63: the node the EDITOR can select for a tree row — the node itself when it is a first-level node, or
 * the first-level WORKFLOW node that contains it when it is nested (the only ids that exist on the canvas,
 * same rule as the BOM highlight, `bomTree.js#resolveHighlightTargetNodeId`). Empty string when unknown.
 * @param {string} nodeId
 * @param {Node[]} nodes Top-level nodes
 * @returns {string}
 */
const resolveTopLevelNodeId = (nodeId, nodes) => {
  const level = Array.isArray(nodes) ? nodes : []
  const direct = level.find((node) => node.id === nodeId)
  if (direct) {
    return direct.id
  }
  const contains = (node) => {
    if (!isHydratedSet(node)) {
      return false
    }
    return node.subWorkflow.nodes.some((child) => child.id === nodeId || contains(child))
  }
  const container = level.find((node) => contains(node))
  return container ? container.id : ''
}

/**
 * RN68/RN69: the previous/next chips of the inspector, computed INSIDE the node's level. `previous` are
 * the nodes of the level whose result points at it (in model order, OK told from FAIL), preceded by
 * `{ start: true }` when the node is a root; `next` are its own results: a target node, `end` when the
 * result closes the process (RN12) or `dangling` when it does not resolve (RN13). A hoisted converging
 * job therefore lists the last job of EVERY branch (S84), because the links come from the model, not from
 * where the tree happened to draw it.
 * @param {string} nodeId
 * @param {Node[]} levelNodes The nodes of the level the node lives in (see `findLevelNodes`)
 * Cada enlace viaja con su `label` (el code, o el name, o el id como ultimo recurso: `labelOf`), porque
 * el `nodeId` es un UUID en cualquier proceso real y un chip que lo imprima no dice nada.
 * @returns {{ previous: Object[], next: Object[] }}
 */
const resolveNodeLinks = (nodeId, levelNodes) => {
  const level = Array.isArray(levelNodes) ? levelNodes : []
  const node = findNodeInLevel(level, nodeId)
  if (!node) {
    return { previous: [], next: [] }
  }
  const previous = node.isFirst === true ? [{ start: true }] : []
  level.forEach((candidate) => {
    (candidate.nodeResults || []).forEach((nodeResult) => {
      if (nodeResult.nextNode === nodeId) {
        previous.push({ nodeId: candidate.id, label: labelOf(candidate), branch: nodeResult.operationResult === 'OK' ? BRANCH.OK : BRANCH.FAIL })
      }
    })
  })
  const next = resolveChildBranches(node, level).map((child) => ({
    nodeId: child.target ? child.target.id : '',
    label: child.target ? labelOf(child.target) : '',
    branch: child.branch,
    end: !child.nodeResult.nextNode,
    dangling: child.dangling
  }))
  return { previous, next }
}

/**
 * The hydrated set whose subworkflow DIRECTLY holds the node — the level's container — or null when the
 * node is a first-level node (or is nowhere).
 * @param {string} nodeId
 * @param {Node[]} nodes Top-level nodes
 * @returns {Node|null}
 */
const findContainerNode = (nodeId, nodes) => {
  const search = (level) => {
    if (!Array.isArray(level)) {
      return null
    }
    for (const node of level) {
      if (isHydratedSet(node)) {
        if (node.subWorkflow.nodes.some((child) => child.id === nodeId)) {
          return node
        }
        const found = search(node.subWorkflow.nodes)
        if (found) {
          return found
        }
      }
    }
    return null
  }
  return search(nodes)
}

/**
 * PO review 2026-09-11: the operations that MUST be finished before this one — every node an OK result
 * chain leads from, at any distance, within its level; and, for a nested node, the same for each set it
 * lives inside, because nothing inside a set starts before what precedes the set. A FAIL result is a
 * rework path, not a requirement, so it is never followed. And a predecessor only counts if the process
 * can reach it from its start through OK results WITHOUT passing through the node: a rework operation
 * comes back to the node through an OK result, but it only exists because the node failed, so it is not
 * "before" it — while an earlier operation the node reworks to on failure still is. Cycle-safe, and the
 * node itself is never in the answer.
 * @param {string} nodeId
 * @param {Node[]} nodes Top-level nodes
 * @returns {Set<string>} The ids of the required predecessors
 */
const collectRequiredPredecessorIds = (nodeId, nodes) => {
  const required = new Set()
  // The nodes of the level the process reaches from its roots through OK results, never entering `blockedId`.
  const okReachableAvoiding = (level, blockedId) => {
    const reached = new Set()
    const pending = resolveLevelRoots(level).roots.map((root) => root.id).filter((id) => id !== blockedId)
    while (pending.length > 0) {
      const currentId = pending.shift()
      if (reached.has(currentId)) {
        continue
      }
      reached.add(currentId);
      (findNodeInLevel(level, currentId)?.nodeResults || []).forEach((nodeResult) => {
        if (nodeResult.operationResult === 'OK' && nodeResult.nextNode && nodeResult.nextNode !== blockedId && !reached.has(nodeResult.nextNode)) {
          pending.push(nodeResult.nextNode)
        }
      })
    }
    return reached
  }
  const walkLevel = (targetId, level) => {
    const upstream = okReachableAvoiding(level, targetId)
    const pending = [targetId]
    const seen = new Set()
    while (pending.length > 0) {
      const currentId = pending.shift()
      level.forEach((candidate) => {
        if (seen.has(candidate.id) || !upstream.has(candidate.id)) {
          return
        }
        const leadsHere = (candidate.nodeResults || []).some((nodeResult) => nodeResult.nextNode === currentId && nodeResult.operationResult === 'OK')
        if (leadsHere) {
          seen.add(candidate.id)
          required.add(candidate.id)
          pending.push(candidate.id)
        }
      })
    }
  }
  let currentId = nodeId
  let level = findLevelNodes(currentId, nodes)
  while (level.length > 0) {
    walkLevel(currentId, level)
    const container = findContainerNode(currentId, nodes)
    if (!container) {
      break
    }
    currentId = container.id
    level = findLevelNodes(currentId, nodes)
  }
  required.delete(nodeId)
  return required
}

const normalizeQuery = (query) => (typeof query === 'string' ? query.trim().toLowerCase() : '')
const SEARCHABLE_KINDS = new Set([PROCESS_TREE_ROW_KIND.JOB, PROCESS_TREE_ROW_KIND.SET])

/**
 * RN44/RN47: a row matches the query by OPERATION NAME and TECHNOLOGY — never by the job code — and by
 * BOM COMPONENT CODE only while the Material button is on (the BOM is not even requested before, RN49).
 * Case-insensitive, like the rest of the editor. An empty query matches everything. Synthetic, reference
 * and material rows never match by themselves: they are shown as ancestors/content of a match.
 * @param {Object} row
 * @param {string} query
 * @param {{ materialEnabled: boolean }} options
 * @returns {boolean}
 */
const matchesProcessTreeQuery = (row, query, { materialEnabled = false } = {}) => {
  const needle = normalizeQuery(query)
  if (needle === '') {
    return true
  }
  if (!row || !SEARCHABLE_KINDS.has(row.kind)) {
    return false
  }
  const haystacks = [row.name || '', row.technology || '']
  if (materialEnabled) {
    haystacks.push(...(row.materialCodes || []))
  }
  return haystacks.some((value) => String(value).toLowerCase().includes(needle))
}

/**
 * RN39/RN45/RN46: decides which rows are on screen. Without a query, a row is hidden when any ancestor is
 * collapsed. With a query, only the matches and their ancestors (the sets and Parallel rows that lead to
 * them, RN45) plus the material rows of a matching job are kept — never flattened — and the collapsed
 * ancestors still hide their content: a collapsed set that holds a match is returned with
 * `containsMatch: true` and stays collapsed (RN46). Searching never expands anything. The input rows are
 * not mutated (the returned rows are shallow copies carrying `containsMatch`). A collapsed set hides its
 * CONTENT only: the operations that FOLLOW it hang from the same key and stay on screen (S8/S24).
 * @param {Object[]} rows Full row list of `buildProcessTreeRows`
 * @param {{ isCollapsed: function(Object): boolean, query: string, materialEnabled: boolean }} options
 * @returns {{ visibleRows: Object[], hasMatches: boolean }}
 */
const selectVisibleRows = (rows, { isCollapsed = () => false, query = '', materialEnabled = false } = {}) => {
  const list = Array.isArray(rows) ? rows : []
  const byKey = new Map(list.map((row) => [row.key, row]))
  const ancestorsOf = (row) => {
    const chain = []
    let parent = byKey.get(row.parentKey)
    while (parent) {
      chain.push(parent)
      parent = byKey.get(parent.parentKey)
    }
    return chain
  }
  /**
   * RN39/RN40: what a collapsed row folds away. A SET folds its CONTENT (`contentOf`), never what comes
   * after it: both hang from the set's key, so the sequence would disappear with the set otherwise
   * (S8/S24). A Parallel row folds every branch under it. Nothing else folds.
   */
  const foldsInto = (row, parent) => (parent.kind === PROCESS_TREE_ROW_KIND.SET
    ? row.contentOf === parent.key
    : parent.kind === PROCESS_TREE_ROW_KIND.PARALLEL || parent.kind === PROCESS_TREE_ROW_KIND.BRANCH)

  const hiddenByCollapse = (row) => {
    let child = row
    let parent = byKey.get(child.parentKey)
    while (parent) {
      if (parent.collapsible && isCollapsed(parent) && foldsInto(child, parent)) {
        return true
      }
      child = parent
      parent = byKey.get(child.parentKey)
    }
    return false
  }

  const searching = normalizeQuery(query) !== ''
  if (!searching) {
    return {
      visibleRows: list.filter((row) => !hiddenByCollapse(row)).map((row) => ({ ...row, containsMatch: false })),
      hasMatches: true
    }
  }

  const matches = list.filter((row) => matchesProcessTreeQuery(row, query, { materialEnabled }))
  const kept = new Set()
  const holdsMatch = new Set()
  matches.forEach((match) => {
    kept.add(match.key)
    ancestorsOf(match).forEach((ancestor) => {
      kept.add(ancestor.key)
      holdsMatch.add(ancestor.key)
    })
    list.forEach((row) => {
      if (row.kind === PROCESS_TREE_ROW_KIND.MATERIAL && row.parentKey === match.key) {
        kept.add(row.key)
      }
    })
  })
  const visibleRows = list
    .filter((row) => kept.has(row.key) && !hiddenByCollapse(row))
    .map((row) => ({ ...row, containsMatch: holdsMatch.has(row.key) && row.collapsible && isCollapsed(row) }))
  return { visibleRows, hasMatches: matches.length > 0 }
}

/**
 * RN131 (story #150): whether the material of a job may be edited from the tree. Four conditions, all
 * of them necessary — the permission, the workflow being in edit mode, the job being top level, and the
 * job not being effectively subcontracted. A nested job is shown whole and read-only: its material
 * belongs to its own process, and editing it here would write into a model this editor does not own.
 *
 * @param {{hasResourcesPermission: boolean, editAllowed: boolean, isTopLevel: boolean, jobReference: Object}} input
 * @returns {boolean}
 */
const resolveMaterialEditable = ({ hasResourcesPermission, editAllowed, isTopLevel, jobReference }) =>
  hasResourcesPermission === true && editAllowed === true && isTopLevel === true && !isExternalEffective(jobReference)

/**
 * RN131/POL105: says WHY the material is not editable, so a read-only panel never looks broken. The
 * order matters — it answers with the reason closest to the user's own action first, the same order
 * `resolveExternCheckboxGating` already uses.
 *
 * @param {{editAllowed: boolean, isTopLevel: boolean, jobReference: Object, notEditingText: string, nestedText: string, externalText: string}} input
 * @returns {string} the reason, or an empty string when it IS editable
 */
const resolveMaterialEditHint = ({ editAllowed, isTopLevel, jobReference, notEditingText, nestedText, externalText }) => {
  if (editAllowed !== true) {
    return notEditingText
  }
  if (isTopLevel !== true) {
    return nestedText
  }
  if (isExternalEffective(jobReference)) {
    return externalText
  }
  return ''
}

/**
 * RN64: the sets a row lives INSIDE, innermost first — the only ones that have to be opened for it to be
 * reachable. A set the row merely comes after is left as it was: opening it would show what nobody asked for.
 * Moved from `ProcessTreeView.jsx` (story 167): the mini tree needs it too.
 */
const containingSetKeys = (row, byKey) => {
  const keys = []
  let child = row
  let parent = byKey.get(child.parentKey)
  while (parent) {
    if (parent.kind === PROCESS_TREE_ROW_KIND.SET && child.contentOf === parent.key) {
      keys.push(parent.key)
    }
    child = parent
    parent = byKey.get(child.parentKey)
  }
  return keys
}

export {
  containingSetKeys,
  PROCESS_TREE_ROW_KIND,
  BRANCH,
  LANE_EVENT,
  parseTaskDurationMs,
  computeJobTimeMs,
  resolveLevelRoots,
  resolveChildBranches,
  resolveParallelJoin,
  collectJobNodes,
  collectUnhydratedSetNodeIds,
  computeSetSummary,
  collectProcessTotals,
  buildProcessTreeRows,
  matchesProcessTreeQuery,
  selectVisibleRows,
  findLevelNodes,
  findContainerNode,
  resolveTopLevelNodeId,
  resolveMaterialEditable,
  resolveMaterialEditHint,
  resolveNodeLinks,
  collectRequiredPredecessorIds
}
